<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useI18n } from '@/i18n'
import { ElMessage } from 'element-plus'
import { api } from '@/api'

const { t } = useI18n()

const form = ref({
  name: '',
  email: '',
  subject: '',
  message: ''
})

// Honeypot field (should stay empty - bots fill it)
const honeypot = ref('')

// Anti-spam token from backend
const formToken = ref('')

const captchaAnswer = ref('')
const captcha = ref(generateCaptcha())
const loading = ref(false)
const submitted = ref(false)

function generateCaptcha() {
  // Generate numbers between 1 and 20 for more variety
  const num1 = Math.floor(Math.random() * 20) + 1
  const num2 = Math.floor(Math.random() * 20) + 1
  return {
    num1,
    num2,
    question: `${num1} + ${num2} = ?`,
    answer: num1 + num2
  }
}

function refreshCaptcha() {
  captcha.value = generateCaptcha()
  captchaAnswer.value = ''
}

const isValidCaptcha = computed(() => {
  const answer = parseInt(captchaAnswer.value)
  return !isNaN(answer) && answer === captcha.value.answer
})

const isFormValid = computed(() => {
  return form.value.name.trim().length >= 2 &&
         form.value.email.trim() !== '' &&
         form.value.email.includes('@') &&
         form.value.email.includes('.') &&
         form.value.subject.trim() !== '' &&
         form.value.message.trim().length >= 10 &&
         isValidCaptcha.value &&
         formToken.value !== ''
})

const subjects = [
  { value: 'general', label: 'contact.subject.general' },
  { value: 'support', label: 'contact.subject.support' },
  { value: 'billing', label: 'contact.subject.billing' },
  { value: 'partnership', label: 'contact.subject.partnership' },
  { value: 'other', label: 'contact.subject.other' }
]

// Fetch anti-spam token on mount
async function fetchFormToken() {
  try {
    const response = await api.get('/contact/token')
    formToken.value = response.data.token
  } catch (error) {
    console.error('Failed to fetch form token:', error)
    // Generate a fallback token for graceful degradation
    formToken.value = `${Date.now()}.fallback`
  }
}

async function submitForm() {
  if (!isFormValid.value) {
    ElMessage.warning(t('contact.form.invalid'))
    return
  }
  
  loading.value = true
  
  try {
    const payload = {
      name: form.value.name.trim(),
      email: form.value.email.trim(),
      subject: form.value.subject,
      message: form.value.message.trim(),
      captcha_answer: parseInt(captchaAnswer.value),
      captcha_num1: captcha.value.num1,
      captcha_num2: captcha.value.num2,
      form_token: formToken.value,
      website: honeypot.value  // Honeypot - should be empty
    }
    
    const response = await api.post('/contact/submit', payload)
    
    if (response.data.success) {
      submitted.value = true
      ElMessage.success(t('contact.form.success'))
      
      // Reset form
      form.value = { name: '', email: '', subject: '', message: '' }
      captchaAnswer.value = ''
      honeypot.value = ''
      refreshCaptcha()
    } else {
      ElMessage.error(t('contact.form.error'))
    }
  } catch (error: any) {
    const message = error.response?.data?.detail || t('contact.form.error')
    ElMessage.error(message)
    
    // Refresh captcha on error
    refreshCaptcha()
    
    // Refresh token if expired
    if (error.response?.status === 400) {
      await fetchFormToken()
    }
  } finally {
    loading.value = false
  }
}

// Reset form for new message
async function resetForm() {
  submitted.value = false
  await fetchFormToken()
}

onMounted(() => {
  fetchFormToken()
})
</script>

<template>
  <div class="contact-view">
    <div class="contact-container">
      <div class="contact-header">
        <h1>{{ t('contact.title') }}</h1>
        <p>{{ t('contact.subtitle') }}</p>
      </div>
      
      <div class="contact-content">
        <!-- Contact Info -->
        <div class="contact-info">
          <div class="info-card">
            <span class="info-icon">📧</span>
            <div class="info-content">
              <h3>Email</h3>
              <a href="mailto:request@m2v.io">request@m2v.io</a>
            </div>
          </div>
          
          <div class="info-card">
            <span class="info-icon">⏰</span>
            <div class="info-content">
              <h3>{{ t('contact.response_time') }}</h3>
              <p>{{ t('contact.response_delay') }}</p>
            </div>
          </div>
          
          <div class="info-card">
            <span class="info-icon">🌍</span>
            <div class="info-content">
              <h3>{{ t('contact.languages') }}</h3>
              <p>Français, English</p>
            </div>
          </div>
        </div>
        
        <!-- Contact Form -->
        <div class="contact-form-wrapper">
          <div v-if="submitted" class="success-message">
            <div class="success-icon">✅</div>
            <h2>{{ t('contact.form.sent_title') }}</h2>
            <p>{{ t('contact.form.sent_message') }}</p>
            <el-button type="primary" @click="resetForm">
              {{ t('contact.form.new_message') }}
            </el-button>
          </div>
          
          <form v-else @submit.prevent="submitForm" class="contact-form">
            <div class="form-row">
              <div class="form-group">
                <label for="name">{{ t('contact.form.name') }} *</label>
                <el-input 
                  id="name"
                  v-model="form.name" 
                  :placeholder="t('contact.form.name_placeholder')"
                />
              </div>
              
              <div class="form-group">
                <label for="email">{{ t('contact.form.email') }} *</label>
                <el-input 
                  id="email"
                  v-model="form.email" 
                  type="email"
                  :placeholder="t('contact.form.email_placeholder')"
                />
              </div>
            </div>
            
            <div class="form-group">
              <label for="subject">{{ t('contact.form.subject') }} *</label>
              <el-select 
                id="subject"
                v-model="form.subject" 
                :placeholder="t('contact.form.subject_placeholder')"
                style="width: 100%"
              >
                <el-option
                  v-for="subj in subjects"
                  :key="subj.value"
                  :value="subj.value"
                  :label="t(subj.label)"
                />
              </el-select>
            </div>
            
            <div class="form-group">
              <label for="message">{{ t('contact.form.message') }} *</label>
              <el-input
                id="message"
                v-model="form.message"
                type="textarea"
                :rows="5"
                :placeholder="t('contact.form.message_placeholder')"
              />
              <span class="char-count">{{ form.message.length }} / 10 min</span>
            </div>
            
            <!-- Honeypot field (hidden from users, bots fill it) -->
            <div class="honey-field" aria-hidden="true">
              <label for="website">Website</label>
              <input 
                id="website"
                v-model="honeypot"
                type="text"
                name="website"
                autocomplete="off"
                tabindex="-1"
              />
            </div>

            <!-- Captcha -->
            <div class="form-group captcha-group">
              <label>{{ t('contact.form.captcha') }} *</label>
              <p class="captcha-hint">{{ t('contact.form.captcha_hint') }}</p>
              <div class="captcha-row">
                <div class="captcha-question">
                  {{ captcha.question }}
                </div>
                <el-input 
                  v-model="captchaAnswer"
                  :placeholder="t('contact.form.captcha_placeholder')"
                  class="captcha-input"
                  type="number"
                />
                <el-button 
                  type="info" 
                  @click="refreshCaptcha"
                  class="captcha-refresh"
                  :title="t('contact.form.captcha_refresh')"
                >
                  🔄
                </el-button>
              </div>
            </div>
            
            <el-button 
              type="primary" 
              native-type="submit"
              :loading="loading"
              :disabled="!isFormValid"
              class="submit-btn"
            >
              {{ t('contact.form.send') }}
            </el-button>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.contact-view {
  min-height: calc(100vh - 64px);
  padding: var(--m2v-space-12) var(--m2v-space-6);
}

.contact-container {
  max-width: 1000px;
  margin: 0 auto;
}

.contact-header {
  text-align: center;
  margin-bottom: var(--m2v-space-10);
}

.contact-header h1 {
  font-family: var(--m2v-font-display);
  font-size: 2.5rem;
  margin-bottom: var(--m2v-space-4);
}

.contact-header p {
  color: var(--m2v-text-muted);
  font-size: 1.125rem;
}

.contact-content {
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: var(--m2v-space-8);
}

/* Contact Info */
.contact-info {
  display: flex;
  flex-direction: column;
  gap: var(--m2v-space-4);
}

.info-card {
  display: flex;
  align-items: flex-start;
  gap: var(--m2v-space-4);
  padding: var(--m2v-space-5);
  background: var(--m2v-bg-surface);
  border: 1px solid var(--m2v-border-subtle);
  border-radius: var(--m2v-radius-lg);
}

.info-icon {
  font-size: 1.5rem;
}

.info-content h3 {
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: var(--m2v-space-1);
}

.info-content p,
.info-content a {
  color: var(--m2v-text-muted);
  font-size: 0.875rem;
}

.info-content a:hover {
  color: var(--m2v-primary-light);
}

/* Form */
.contact-form-wrapper {
  background: var(--m2v-bg-surface);
  border: 1px solid var(--m2v-border-subtle);
  border-radius: var(--m2v-radius-xl);
  padding: var(--m2v-space-8);
}

.success-message {
  text-align: center;
  padding: var(--m2v-space-10);
}

.success-icon {
  font-size: 4rem;
  margin-bottom: var(--m2v-space-4);
}

.success-message h2 {
  font-size: 1.5rem;
  margin-bottom: var(--m2v-space-3);
}

.success-message p {
  color: var(--m2v-text-muted);
  margin-bottom: var(--m2v-space-6);
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--m2v-space-4);
}

.form-group {
  margin-bottom: var(--m2v-space-5);
}

.form-group label {
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: var(--m2v-space-2);
  color: var(--m2v-text-primary);
}

.char-count {
  display: block;
  text-align: right;
  font-size: 0.75rem;
  color: var(--m2v-text-muted);
  margin-top: var(--m2v-space-1);
}

/* Honeypot field - hidden from users */
.honey-field {
  position: absolute;
  left: -9999px;
  opacity: 0;
  height: 0;
  overflow: hidden;
  pointer-events: none;
}

.captcha-group {
  background: var(--m2v-bg-elevated);
  padding: var(--m2v-space-4);
  border-radius: var(--m2v-radius-md);
}

.captcha-hint {
  font-size: 0.75rem;
  color: var(--m2v-text-muted);
  margin: 0 0 var(--m2v-space-2) 0;
}

.captcha-row {
  display: flex;
  align-items: center;
  gap: var(--m2v-space-3);
}

.captcha-question {
  background: var(--m2v-bg-surface);
  padding: var(--m2v-space-3) var(--m2v-space-4);
  border-radius: var(--m2v-radius-md);
  font-weight: 600;
  font-family: monospace;
  font-size: 1.125rem;
  border: 1px solid var(--m2v-border-subtle);
}

.captcha-input {
  width: 100px;
}

.captcha-refresh {
  flex-shrink: 0;
}

.submit-btn {
  width: 100%;
  margin-top: var(--m2v-space-4);
}

@media (max-width: 768px) {
  .contact-content {
    grid-template-columns: 1fr;
  }
  
  .form-row {
    grid-template-columns: 1fr;
  }
  
  .captcha-row {
    flex-wrap: wrap;
  }
  
  .captcha-input {
    flex: 1;
    min-width: 80px;
  }
}
</style>
